import { createFileRoute } from "@tanstack/react-router";
import { z } from "zod";
import createAxios from "@/lib/axios";
import type { Topic } from "@/types";
import { useForm, useFieldArray } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Form } from "@/components/ui/form";
import { Button } from "@/components/ui/button";
import { PlusIcon, SendIcon } from "lucide-react";
import { formSchema, FormType } from "./quizSchemas";
import { splitType, joinType } from "./quizTypeUtils";
import { QuizQuestionSpec } from "./QuizQuestionSpec";
import { Card } from "@/components/ui/card";

export const Route = createFileRoute("/dashboard/quiz/new/")({
  validateSearch: (search) =>
    z
      .object({
        subject_id: z.number(),
      })
      .parse(search),
  loaderDeps: ({ search }) => ({ subject_id: search.subject_id }),
  loader: async ({ deps: { subject_id } }) => {
    const client = await createAxios();
    const res = await client.get<{ topics: Topic[] }>(
      `${import.meta.env.VITE_BACKEND_URL}/subjects/${subject_id}/topics`,
    );
    const topics = res.data.topics;
    if (!topics) {
      throw new Error("No topics found");
    }
    // Ensure all unit values are trimmed strings
    const units = Array.from(
      new Set(
        topics.map((topic) => String(topic.unit ?? "Uncategorized").trim()),
      ),
    ).sort();
    const topicsByUnit = topics.reduce<Record<string, Topic[]>>(
      (acc, topic) => {
        const unit = String(topic.unit ?? "Uncategorized").trim();
        if (!acc[unit]) {
          acc[unit] = [];
        }
        acc[unit].push(topic);
        return acc;
      },
      {},
    );
    const questionTypes = [
      "mcq-1mark-memory",
      "mcq-1mark-analytical",
      "mcq-1mark-scenario",
      "mcq-2marks-memory",
      "mcq-2marks-analytical",
      "mcq-2marks-scenario",
      "description-2marks-memory",
      "description-2marks-analytical",
      "description-2marks-scenario",
      "description-4marks-memory",
      "description-4marks-analytical",
      "description-4marks-scenario",
    ];
    return { topicsByUnit, units, questionTypes, subject_id };
  },
  component: () => <NewQuiz />,
});

function NewQuiz() {
  const { units, questionTypes, topicsByUnit } = Route.useLoaderData();
  const navigate = Route.useNavigate();
  const form = useForm<FormType>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      specs: [{ type: "", unit: "", topic_id: undefined }],
    },
  });
  const { fields, append, remove } = useFieldArray({
    control: form.control,
    name: "specs",
  });

  // Helper to update unit and reset topic for a spec
  const handleUnitChange = (idx: number, value: string) => {
    form.setValue(`specs.${idx}.unit`, value);
    form.setValue(`specs.${idx}.topic_id`, undefined); // Reset topic when unit changes
  };

  // Helper to update type from split comboboxes
  const handleTypePartChange = (
    idx: number,
    part: "format" | "marks" | "category",
    value: string,
  ) => {
    const currentType = form.getValues(`specs.${idx}.type`);
    const { format, marks, category } = splitType(currentType || "");
    const newType = joinType(
      part === "format" ? value : format,
      part === "marks" ? value : marks,
      part === "category" ? value : category,
    );
    form.setValue(`specs.${idx}.type`, newType);
  };

  const onSubmit = async (data: FormType) => {
    // Transform data to only use topic_id, picking random topic if only unit is selected
    const specs = data.specs.map((spec) => {
      let topicId: number | undefined = undefined;
      if (spec.topic_id) {
        topicId = spec.topic_id;
      } else if (
        spec.unit &&
        topicsByUnit[spec.unit] &&
        topicsByUnit[spec.unit].length > 0
      ) {
        const topics = topicsByUnit[spec.unit];
        const randomIdx = Math.floor(Math.random() * topics.length);
        topicId = topics[randomIdx].id;
      }
      return {
        type: spec.type,
        topic_id: topicId,
      };
    });
    // Optionally filter out specs where topic_id could not be determined
    const filteredSpecs = specs.filter((s) => typeof s.topic_id === "number");
    const body = { specs: filteredSpecs };
    const client = await createAxios();
    const {
      data: { id: qaSetId },
    } = await client.post<{ id: string }>(
      import.meta.env.VITE_BACKEND_URL + "/qa-set",
      body,
      {
        headers: {
          "Content-Type": "application/json",
        },
      },
    );
    await navigate({
      to: "/dashboard/quiz/$id",
      params: { id: qaSetId },
    });
  };

  return (
    <div className="light mx-auto w-full bg-primary-foreground p-6 text-primary">
      <h1 className="mb-6 text-4xl font-bold">Create New Quiz</h1>
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
          <div className="flex flex-col gap-4">
            {fields.map((field, idx) => (
              <Card key={field.id} className="p-4">
                <QuizQuestionSpec
                  idx={idx}
                  form={form}
                  questionTypes={questionTypes}
                  units={units}
                  topicsByUnit={topicsByUnit}
                  remove={remove}
                  handleUnitChange={handleUnitChange}
                  handleTypePartChange={handleTypePartChange}
                />
              </Card>
            ))}
          </div>
          <div className="flex flex-row gap-4">
            <Button
              type="button"
              onClick={() => {
                const lastSpec = fields[fields.length - 1];
                append({
                  type: lastSpec?.type || questionTypes[0],
                  unit: lastSpec?.unit || units[0] || "",
                  topic_id: lastSpec?.topic_id,
                });
              }}
              variant="outline"
            >
              Add Question
              <PlusIcon className="ml-2 size-4" />
            </Button>
            <Button type="submit" className="bg-green-600 text-white">
              Submit
              <SendIcon className="ml-2 size-4" />
            </Button>
          </div>
        </form>
      </Form>
    </div>
  );
}
